import { useEffect, useRef, useState } from "react";
import api from "../api";

const sid = () => {
  let s = localStorage.getItem("assistant_sid");
  if (!s) { s = Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem("assistant_sid", s); }
  return s;
};

export const AssistantChat = () => {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(false);
  const [rec, setRec] = useState(false);
  const [speak, setSpeak] = useState(() => localStorage.getItem("assistant_speak") === "1");
  const [mics, setMics] = useState([]);
  const [micId, setMicId] = useState(() => localStorage.getItem("assistant_mic") || "");
  const [watch, setWatch] = useState({ count: 0, text: "" });
  const recRef = useRef(null);
  const bodyRef = useRef(null);
  const session = useRef(sid());

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const r = await api.get(`/assistant/history/${session.current}`);
        setMsgs((r.data.messages || []).filter((m) => m.role !== "tool")); setPending(r.data.pending);
      } catch {}
      const today = new Date().toISOString().slice(0, 10);
      try {
        if (localStorage.getItem("assistant_briefing_date") !== today) {
          localStorage.setItem("assistant_briefing_date", today);
          const b = await api.get("/assistant/briefing");
          push("assistant", b.data.text);
          markSeen(b.data.alerts_text);
          if (speak) sayAloud(b.data.text);
        } else {
          const w = await api.get("/assistant/watch");
          if (w.data.count > 0 && w.data.text !== (localStorage.getItem("assistant_watch_seen") || "")) {
            push("assistant", w.data.text);
          }
          markSeen(w.data.text);
        }
      } catch {}
    })();
  }, [open]);
  useEffect(() => { bodyRef.current?.scrollTo(0, 99999); }, [msgs, open, busy]);

  useEffect(() => {
    const seen = () => localStorage.getItem("assistant_watch_seen") || "";
    const load = () => api.get("/assistant/watch").then((r) =>
      setWatch(r.data.text && r.data.text === seen() ? { ...r.data, count: 0 } : r.data)).catch(() => {});
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, []);

  const markSeen = (alertsText) => {
    localStorage.setItem("assistant_watch_seen", alertsText || "");
    setWatch((w) => ({ ...w, count: 0 }));
  };

  const loadMics = async (askPermission = false) => {
    try {
      let devs = await navigator.mediaDevices.enumerateDevices();
      if (askPermission && !devs.some((d) => d.kind === "audioinput" && d.label)) {
        const s = await navigator.mediaDevices.getUserMedia({ audio: true });
        s.getTracks().forEach((t) => t.stop());
        devs = await navigator.mediaDevices.enumerateDevices();
      }
      setMics(devs.filter((d) => d.kind === "audioinput"));
    } catch {}
  };
  useEffect(() => {
    if (!open) return;
    loadMics(false);
    navigator.mediaDevices?.addEventListener?.("devicechange", loadMics);
    return () => navigator.mediaDevices?.removeEventListener?.("devicechange", loadMics);
  }, [open]);

  const chooseMic = (id) => {
    setMicId(id);
    if (id) localStorage.setItem("assistant_mic", id); else localStorage.removeItem("assistant_mic");
  };

  const push = (role, t) => setMsgs((m) => [...m, { role, text: t }]);

  const sayAloud = (t) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const clean = t.replace(/\*\*|__|[«»#•]/g, "").replace(/https?:\/\/\S+/g, "");
    const u = new SpeechSynthesisUtterance(clean);
    u.lang = "it-IT";
    const v = window.speechSynthesis.getVoices().find((x) => x.lang?.startsWith("it"));
    if (v) u.voice = v;
    u.rate = 1.05;
    window.speechSynthesis.speak(u);
  };
  const say = (t) => { push("assistant", t); if (speak) sayAloud(t); };
  const toggleSpeak = () => {
    const nv = !speak;
    setSpeak(nv); localStorage.setItem("assistant_speak", nv ? "1" : "0");
    if (!nv) window.speechSynthesis?.cancel(); else sayAloud("Voce attiva!");
  };

  const send = async () => {
    const t = text.trim();
    if (!t || busy) return;
    setText(""); push("user", t); setBusy(true);
    try {
      const r = await api.post("/assistant/chat", { session_id: session.current, message: t });
      say(r.data.reply); setPending(r.data.needs_confirmation);
    } catch (e) {
      say("⚠️ " + (e.response?.data?.detail || e.message));
    }
    setBusy(false);
  };

  const toggleMic = async () => {
    if (rec) { recRef.current?.stop(); return; }
    try {
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: micId ? { deviceId: { exact: micId } } : true });
      } catch {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      }
      loadMics(false);
      const mr = new MediaRecorder(stream);
      const chunks = [];
      mr.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRec(false); setBusy(true);
        try {
          const fd = new FormData();
          fd.append("file", new Blob(chunks, { type: mr.mimeType || "audio/webm" }), "voce.webm");
          const r = await api.post("/assistant/transcribe", fd);
          setText((t) => (t ? t + " " : "") + (r.data.text || ""));
        } catch (e) { push("assistant", "⚠️ Dettatura non riuscita: " + (e.response?.data?.detail || e.message)); }
        setBusy(false);
      };
      recRef.current = mr; mr.start(); setRec(true);
    } catch { push("assistant", "⚠️ Microfono non disponibile o permesso negato."); }
  };

  const showPlan = async () => {
    setBusy(true);
    try {
      let r = await api.get("/assistant/plan");
      if (r.data.missing) r = await api.post("/assistant/plan/generate");
      const m = r.data.month || "";
      push("assistant", `📈 PIANO STRATEGICO — ${m}\n\n${r.data.text}`);
      if (r.data.review) push("assistant", `🔎 VERIFICA DI FINE MESE — ${m}\n\n${r.data.review}`);
    } catch (e) { push("assistant", "⚠️ Non riesco a preparare il piano ora: " + (e.response?.data?.detail || e.message)); }
    setBusy(false);
  };

  const confirm = async (accept) => {
    setBusy(true); setPending(false);
    try {
      const r = await api.post("/assistant/confirm", { session_id: session.current, accept });
      say(r.data.reply);
    } catch (e) { say("⚠️ " + (e.response?.data?.detail || e.message)); }
    setBusy(false);
  };

  return (
    <>
      <button className="ai-fab" onClick={() => setOpen(!open)} title="Elon" data-testid="assistant-fab">
        <img src="/assistant-elon.png" alt="Elon" />
        {watch.count > 0 && <span className="ai-fab-dot" data-testid="assistant-alert-badge">{watch.count > 99 ? "99+" : watch.count}</span>}
      </button>
      {open && (
        <div className="ai-chat" data-testid="assistant-panel">
          <div className="ai-chat-head">
            <img src="/assistant-elon.png" alt="" style={{ width: 34, height: 34, objectFit: "contain" }} />
            <b>Elon</b>
            <span className="muted" style={{ fontSize: 11 }}>domande · appuntamenti · incarichi · note</span>
            <div className="spacer" />
            <button className="btn sm" onClick={showPlan} disabled={busy} title="Piano strategico del mese" data-testid="assistant-plan-btn">📈</button>
            <button className="btn sm" onClick={toggleSpeak} title={speak ? "Disattiva voce" : "Leggi le risposte ad alta voce"} data-testid="assistant-speak-toggle">{speak ? "🔊" : "🔇"}</button>
            <button className="btn sm" onClick={() => setOpen(false)} data-testid="assistant-close">✕</button>
          </div>
          <div className="ai-chat-body" ref={bodyRef} data-testid="assistant-messages">
            {msgs.length === 0 && (
              <div className="muted" style={{ fontSize: 12.5, padding: 8 }}>
                Esempi:<br />• "inserisci appuntamento lunedì con Laura di un'ora in studio da me"<br />
                • "crea incarico: bilancio Rossi entro venerdì"<br />• "che telefono ha Bianchi?"<br />
                • "quali sono le aliquote IRPEF attuali?"<br />• "scrivimi una bozza di email per un cliente in ritardo coi documenti"
              </div>
            )}
            {msgs.map((m, i) => (
              <div key={i} className={`ai-msg ${m.role === "user" ? "me" : ""}`} data-testid={`assistant-msg-${i}`}>{m.text}</div>
            ))}
            {busy && <div className="ai-msg">…</div>}
            {pending && !busy && (
              <div style={{ display: "flex", gap: 6, padding: "4px 6px" }} data-testid="assistant-confirm-bar">
                <button className="btn sm primary" onClick={() => confirm(true)} data-testid="assistant-confirm-yes">✅ Conferma</button>
                <button className="btn sm" onClick={() => confirm(false)} data-testid="assistant-confirm-no">❌ Annulla</button>
              </div>
            )}
          </div>
          <div className="ai-chat-input">
            {mics.length > 0 && (
              <select
                value={micId}
                onChange={(e) => chooseMic(e.target.value)}
                onFocus={() => loadMics(true)}
                disabled={rec || busy}
                title="Scegli quale microfono usare"
                data-testid="assistant-mic-select"
                style={{ maxWidth: 120, fontSize: 11, padding: "4px 4px", borderRadius: 8, border: "1px solid var(--border, #d9d9e3)", background: "transparent", color: "inherit" }}
              >
                <option value="">🎙 Predefinito</option>
                {mics.map((d, i) => (
                  <option key={d.deviceId || i} value={d.deviceId}>{d.label || `Microfono ${i + 1}`}</option>
                ))}
              </select>
            )}
            <button className={`btn sm ${rec ? "danger" : ""}`} onClick={toggleMic} disabled={busy} title={rec ? "Ferma e trascrivi" : "Detta un comando"} data-testid="assistant-mic">{rec ? "⏹" : "🎤"}</button>
            <input value={text} onChange={(e) => setText(e.target.value)} disabled={busy}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder={rec ? "Sto ascoltando…" : pending ? "Oppure rispondi qui…" : "Scrivi un comando…"} data-testid="assistant-input" />
            <button className="btn sm primary" onClick={send} disabled={busy} data-testid="assistant-send">➤</button>
          </div>
        </div>
      )}
    </>
  );
};

export default AssistantChat;
